import myConfig from '@core/app-config/config'

import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  webApi = myConfig.webApi

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(

      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        // const { config, response: { status } } = error
        const { config, response } = error
        const originalRequest = config

        // if (status === 401) {
        if (response && response.status === 401) {
          if (!this.isAlreadyFetchingAccessToken) {
            this.isAlreadyFetchingAccessToken = true
            this.refreshToken().then(r => {
              this.isAlreadyFetchingAccessToken = false

              // Update accessToken in localStorage

              this.setToken(r.data.accessToken)
              this.setRefreshToken(r.data.refreshToken)

              this.onAccessTokenFetched(r.data.accessToken)
            })
          }
          const retryOriginalRequest = new Promise(resolve => {
            this.addSubscriber(accessToken => {
              // Make sure to assign accessToken according to your response.
              // Check: https://pixinvent.ticksy.com/ticket/2413870
              // Change Authorization header
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              resolve(this.axiosIns(originalRequest))
            })
          })
          return retryOriginalRequest
        }
        return Promise.reject(error)
      },
    )
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint,
      { token: this.getToken(), refreshToken: this.getRefreshToken() })
  }

  getProjectDetail(id) {
    return this.axiosIns.get(`${this.webApi}/api/Project/GetDetail`, {
      params: {
        projectId: id,
      },
    })
  }

  SearchProject(keywords, customer, hashtags) {
    return this.axiosIns.post(`${this.webApi}/api/Project/SearchProject`, { Keyword: keywords, customer: customer.value, hashtags })
  }

  getHashtags() {
    return this.axiosIns.get(`${this.webApi}/api/Project/GetHashtags`)
  }

  getCustomers() {
    return this.axiosIns.get(`${this.webApi}/api/Project/GetCustomers`)
  }

  getProjectDetails(items) {
    return this.axiosIns.post(`${this.webApi}/api/Project/GetDetails`, items)
  }

  getProjectPowerPoint(projectId) {
    return this.axiosIns.post(`${this.webApi}/api/Project/GetProjectPowerPoint?projectid=${projectId}`, null, {
      responseType: 'blob',
    })
  }

  getUserGroups() {
    return this.axiosIns.get(`${this.webApi}/api/User/GetUserGroups`)
  }

  findUser(employeeNo) {
    return this.axiosIns.get(`${this.webApi}/api/User/Find`, { params: { employeeNo } })
  }

  addUser(employeeNo, userGroupId, isActive, currentUser) {
    return this.axiosIns.post(`${this.webApi}/api/User/AddUser`, {
      employeeNo,
      userGroupId,
      isActive,
      createBy: currentUser,
    })
  }

  updateUser(employeeNo, userGroupId, isActive) {
    return this.axiosIns.post(`${this.webApi}/api/User/UpdateUser`, {
      employeeNo,
      userGroupId,
      isActive,
    })
  }

  getUser(employeeNo) {
    return this.axiosIns.post(`${this.webApi}/api/User/GetUser`, { employeeNo })
  }

  getUsers() {
    return this.axiosIns.get(`${this.webApi}/api/User/GetUsers`)
  }

  deleteProject(id, coverPicture, powerPointFile) {
    return this.axiosIns.delete(`${this.webApi}/api/Project/DeleteProject`, {
      params: {
        projectId: id,
        coverPictureName: coverPicture,
        powerPointFileName: powerPointFile,
      },
    })
  }

  getProjectById(id) {
    return this.axiosIns.get(`${this.webApi}/api/Project/GetProjectById`, {
      params: {
        projectId: id,
      },
    })
  }

  manageProject(formData) {
    return this.axiosIns.post(`${this.webApi}/api/Project/ManageProject`, formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
  }

  deleteBusinessType(businessTypeId) {
    return this.axiosIns
      .delete(`${this.webApi}/api/BusinessType/Delete`, {
        params: { businessTypeId },
      })
  }

  getAllHashtags() {
    return this.axiosIns.get(`${this.webApi}/api/Hashtag/GetAllHashtags`)
  }

  getHashtagById(hashtagId) {
    return this.axiosIns.get(`${this.webApi}/api/Hashtag/GetHashtagById`, {
      params: { hashtagId },
    })
  }

  addHashtag(data) {
    return this.axiosIns.post(`${this.webApi}/api/Hashtag/Add`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  deleteHashtag(hashtagId) {
    return this.axiosIns.delete(`${this.webApi}/api/Hashtag/Delete`, {
      params: { hashtagId },
    })
  }

  getAllBusinessTypes() {
    return this.axiosIns.get(`${this.webApi}/api/BusinessType/GetAllBusinessTypes`)
  }

  getBusinessTypeById(businessTypeId) {
    return this.axiosIns.get(`${this.webApi}/api/BusinessType/GetBusinessTypeById`, {
      params: { businessTypeId },
    })
  }

  addBusinessType(data) {
    return this.axiosIns.post(`${this.webApi}/api/BusinessType/Add`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  merge(projectId) {
    return this.axiosIns.post(`${this.webApi}/api/Project/Merge`, projectId)
  }

  getMergedFile(fileName) {
    return this.axiosIns.post(`${this.webApi}/api/Project/GetMergedFile?fileName=${fileName}`, null, {
      responseType: 'blob',
    })
  }
}
